/**
 * A cor que segue um TEXTO — derivada, nunca guardada.
 *
 * ── POR QUE UM ARQUIVO E NÃO UMA TABELA ───────────────────────────────────
 *
 * Porque não há coluna de cor em lugar nenhum. Um contato da V3 não tem avatar
 * colorido, uma etiqueta é `text[]` e um card é um REGISTRO. A V2 mantinha uma
 * paleta por nome e alguém tinha de a manter em dia; aqui a cor sai de um hash do
 * próprio texto, então a mesma pessoa tem sempre a mesma cor e uma etiqueta nova
 * nasce com a dela sem ninguém escolher nada.
 *
 * Aqui e não em `features/`: cor é assunto de `design/` (F-ADR-001), e as duas
 * pontas que a usam — o rosto e a pílula de etiqueta — teriam duas contas
 * diferentes do mesmo hash no dia seguinte.
 *
 * ── POR QUE `light-dark()` E NÃO UM `dark:` ───────────────────────────────
 *
 * Porque a cor é calculada em tempo de execução e entra por `style`, onde não há
 * variante do Tailwind que alcance. `:root` declara `color-scheme` nos dois temas
 * (`tokens.css`), que é exatamente o que `light-dark()` lê — a mesma linha serve
 * os dois sem o componente saber em qual está.
 */

/** O matiz estável de um texto. Mesmo cálculo que o rosto sempre fez. */
export function tintHue(seed: string): number {
	let hash = 0;
	for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
	return hash % 360;
}

/** Dois tons do mesmo matiz — o fundo de um rosto sem foto. */
export function tintGradient(seed: string): string {
	const hue = tintHue(seed);
	return `linear-gradient(135deg, hsl(${hue} 42% 62%), hsl(${(hue + 22) % 360} 38% 42%))`;
}

/**
 * O que uma pílula colorida precisa: fundo, borda e um texto que se leia.
 *
 * `type` e não `interface` de propósito: só um alias ganha assinatura de índice
 * implícita, e sem ela o `CSSProperties` da Vue recusa o objeto num `:style`.
 */
export type ChipTint = {
	readonly background: string;
	readonly borderColor: string;
	readonly color: string;
};

/**
 * Uma etiqueta, tingida.
 *
 * Fundo e borda com alfa sobre a superfície do tema — é o que faz a mesma pílula
 * funcionar no claro e no escuro sem duas tabelas. O TEXTO não pode ser alfa (um
 * texto translúcido é um texto que não se lê), então ele escolhe entre dois
 * tons do mesmo matiz conforme o tema.
 */
export function tintChip(seed: string): ChipTint {
	return tintChipHue(tintHue(seed));
}

/**
 * A mesma pílula, quando o MATIZ é escolhido e não derivado.
 *
 * Uma etiqueta ganha a cor do próprio nome porque ninguém a escolheu; um chip do
 * cabeçalho da conversa é o contrário — "Perfil" é violeta e "Funil" é azul
 * porque a V2 os pintou assim, e um hash de "Perfil" daria o tom que desse.
 *
 * Mesma conta, mesmo alfa, mesma escolha de texto por tema: o que muda é só de
 * onde vem o número. Duas fórmulas divergiriam no primeiro dia em que alguém
 * mexesse numa delas — e é exactamente por isso que `tintChip` agora chama esta.
 */
export function tintChipHue(hue: number): ChipTint {
	return {
		background: `hsl(${hue} 62% 50% / 0.15)`,
		borderColor: `hsl(${hue} 62% 50% / 0.28)`,
		color: `light-dark(hsl(${hue} 64% 30%), hsl(${hue} 72% 76%))`,
	};
}
